import type { ThemeTokens, ColorsWithModes } from './theme';
import {
  border,
  backdropBlur,
  breakpoints,
  colors as globalColors,
  motion,
  spacing,
  typography,
  elevation,
  opacity,
} from '~tokens/global';

const colors: ColorsWithModes = {
  onLight: {
    surface: {
      background: {
        gray: {
          subtle: globalColors.neutral.blueGrayLight[100],
          moderate: globalColors.neutral.blueGrayLight[50],
          intense: globalColors.neutral.blueGrayLight[0],
        },
        primary: {
          subtle: globalColors.chromatic.azure.a50,
          intense: globalColors.chromatic.azure[500],
        },
        sea: {
          subtle: globalColors.chromatic.sea[50],
          intense: globalColors.chromatic.sea[800],
        },
        cloud: {
          subtle: globalColors.chromatic.cloud[50],
          intense: globalColors.chromatic.cloud[800],
        },
      },
      border: {
        gray: {
          normal: globalColors.neutral.blueGrayLight[300],
          subtle: globalColors.neutral.blueGrayLight[200],
          muted: globalColors.neutral.blueGrayLight.a912,
        },
        primary: {
          normal: globalColors.chromatic.azure[500],
          muted: globalColors.chromatic.azure.a100,
        },
      },
      text: {
        gray: {
          normal: globalColors.neutral.blueGrayLight[1300],
          subtle: globalColors.neutral.blueGrayLight[1100],
          muted: globalColors.neutral.blueGrayLight[700],
          disabled: globalColors.neutral.blueGrayLight.a932,
        },
        primary: {
          normal: globalColors.chromatic.azure[500],
        },
        onSea: {
          onSubtle: globalColors.chromatic.forest[800],
          onIntense: globalColors.chromatic.forest[200],
        },
        onCloud: {
          onSubtle: globalColors.chromatic.azure[600],
          onIntense: globalColors.chromatic.azure[200],
        },
        staticWhite: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[450],
          muted: globalColors.neutral.white[200],
          disabled: globalColors.neutral.white[100],
        },
        staticBlack: {
          normal: globalColors.neutral.black[500],
          subtle: globalColors.neutral.black[300],
          muted: globalColors.neutral.black[200],
          disabled: globalColors.neutral.black[100],
        },
      },
      icon: {
        gray: {
          normal: globalColors.neutral.blueGrayLight[1300],
          subtle: globalColors.neutral.blueGrayLight[1100],
          muted: globalColors.neutral.blueGrayLight[700],
          disabled: globalColors.neutral.blueGrayLight.a932,
        },
        primary: {
          normal: globalColors.chromatic.azure[500],
        },
        onSea: {
          onSubtle: globalColors.chromatic.forest[600],
          onIntense: globalColors.chromatic.forest[400],
        },
        onCloud: {
          onSubtle: globalColors.chromatic.azure[400],
          onIntense: globalColors.chromatic.azure[300],
        },
        staticWhite: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[450],
          muted: globalColors.neutral.white[200],
          disabled: globalColors.neutral.white[100],
        },
        staticBlack: {
          normal: globalColors.neutral.black[500],
          subtle: globalColors.neutral.black[300],
          muted: globalColors.neutral.black[200],
          disabled: globalColors.neutral.black[100],
        },
      },
    },
    feedback: {
      background: {
        positive: {
          subtle: globalColors.chromatic.emerald.a50,
          intense: globalColors.chromatic.emerald[600],
        },
        negative: {
          subtle: globalColors.chromatic.crimson.a50,
          intense: globalColors.chromatic.crimson[600],
        },
        notice: {
          subtle: globalColors.chromatic.cider.a50,
          intense: globalColors.chromatic.cider[600],
        },
        information: {
          subtle: globalColors.chromatic.sapphire.a50,
          intense: globalColors.chromatic.sapphire[600],
        },
        neutral: {
          subtle: globalColors.neutral.blueGrayLight.a909,
          intense: globalColors.neutral.blueGrayLight[1100],
        },
      },
      border: {
        positive: {
          subtle: globalColors.chromatic.emerald.a100,
          intense: globalColors.chromatic.emerald[700],
        },
        negative: {
          subtle: globalColors.chromatic.crimson.a100,
          intense: globalColors.chromatic.crimson[700],
        },
        notice: {
          subtle: globalColors.chromatic.cider.a100,
          intense: globalColors.chromatic.cider[700],
        },
        information: {
          subtle: globalColors.chromatic.sapphire.a100,
          intense: globalColors.chromatic.sapphire[700],
        },
        neutral: {
          subtle: globalColors.neutral.blueGrayLight.a918,
          intense: globalColors.neutral.blueGrayLight[900],
        },
      },
      text: {
        positive: {
          subtle: globalColors.chromatic.emerald[100],
          intense: globalColors.chromatic.emerald[700],
        },
        negative: {
          subtle: globalColors.chromatic.crimson[100],
          intense: globalColors.chromatic.crimson[600],
        },
        notice: {
          subtle: globalColors.chromatic.cider[100],
          intense: globalColors.chromatic.cider[700],
        },
        information: {
          subtle: globalColors.chromatic.sapphire[100],
          intense: globalColors.chromatic.sapphire[700],
        },
        neutral: {
          subtle: globalColors.neutral.blueGrayLight[500],
          intense: globalColors.neutral.blueGrayLight[1100],
        },
      },
      icon: {
        positive: {
          subtle: globalColors.chromatic.emerald[100],
          intense: globalColors.chromatic.emerald[700],
        },
        negative: {
          subtle: globalColors.chromatic.crimson[100],
          intense: globalColors.chromatic.crimson[600],
        },
        notice: {
          subtle: globalColors.chromatic.cider[100],
          intense: globalColors.chromatic.cider[700],
        },
        information: {
          subtle: globalColors.chromatic.sapphire[100],
          intense: globalColors.chromatic.sapphire[700],
        },
        neutral: {
          subtle: globalColors.neutral.blueGrayLight[500],
          intense: globalColors.neutral.blueGrayLight[1100],
        },
      },
    },
    interactive: {
      background: {
        positive: {
          default: globalColors.chromatic.emerald[600],
          highlighted: globalColors.chromatic.emerald[700],
          disabled: globalColors.chromatic.emerald.a50,
          faded: globalColors.chromatic.emerald.a50,
          fadedHighlighted: globalColors.chromatic.emerald.a100,
        },
        negative: {
          default: globalColors.chromatic.crimson[600],
          highlighted: globalColors.chromatic.crimson[700],
          disabled: globalColors.chromatic.crimson.a50,
          faded: globalColors.chromatic.crimson.a50,
          fadedHighlighted: globalColors.chromatic.crimson.a100,
        },
        notice: {
          default: globalColors.chromatic.cider[600],
          highlighted: globalColors.chromatic.cider[700],
          disabled: globalColors.chromatic.cider.a50,
          faded: globalColors.chromatic.cider.a50,
          fadedHighlighted: globalColors.chromatic.cider.a100,
        },
        information: {
          default: globalColors.chromatic.sapphire[600],
          highlighted: globalColors.chromatic.sapphire[700],
          disabled: globalColors.chromatic.sapphire.a50,
          faded: globalColors.chromatic.sapphire.a50,
          fadedHighlighted: globalColors.chromatic.sapphire.a100,
        },
        neutral: {
          default: globalColors.neutral.blueGrayLight[1000],
          highlighted: globalColors.neutral.blueGrayLight[1100],
          disabled: globalColors.neutral.blueGrayLight.a918,
          faded: globalColors.neutral.blueGrayLight.a912,
          fadedHighlighted: globalColors.neutral.blueGrayLight.a918,
        },
        gray: {
          default: globalColors.neutral.blueGrayLight.a906,
          highlighted: globalColors.neutral.blueGrayLight.a912,
          disabled: globalColors.neutral.blueGrayLight.a906,
          faded: globalColors.neutral.blueGrayLight.a906,
          fadedHighlighted: globalColors.neutral.blueGrayLight.a909,
          ghost: globalColors.neutral.blueGrayLight.a1,
        },
        primary: {
          default: globalColors.chromatic.azure[500],
          highlighted: globalColors.chromatic.azure[600],
          disabled: globalColors.chromatic.azure.a100,
          faded: globalColors.chromatic.azure.a50,
          fadedHighlighted: globalColors.chromatic.azure.a100,
        },
        staticBlack: {
          default: globalColors.neutral.black[500],
          highlighted: globalColors.neutral.black[500],
          disabled: globalColors.neutral.black[200],
          faded: globalColors.neutral.black[50],
          fadedHighlighted: globalColors.neutral.black[100],
          ghost: globalColors.neutral.black[1],
        },
        staticWhite: {
          default: globalColors.neutral.white[500],
          highlighted: globalColors.neutral.white[400],
          disabled: globalColors.neutral.white[50],
          faded: globalColors.neutral.white[50],
          fadedHighlighted: globalColors.neutral.white[100],
          ghost: globalColors.neutral.white[1],
        },
      },
      border: {
        positive: {
          default: globalColors.chromatic.emerald[600],
          highlighted: globalColors.chromatic.emerald[700],
          disabled: globalColors.chromatic.emerald.a100,
          faded: globalColors.chromatic.emerald.a100,
        },
        negative: {
          default: globalColors.chromatic.crimson[600],
          highlighted: globalColors.chromatic.crimson[700],
          disabled: globalColors.chromatic.crimson.a100,
          faded: globalColors.chromatic.crimson.a100,
        },
        notice: {
          default: globalColors.chromatic.cider[600],
          highlighted: globalColors.chromatic.cider[700],
          disabled: globalColors.chromatic.cider.a100,
          faded: globalColors.chromatic.cider.a100,
        },
        information: {
          default: globalColors.chromatic.sapphire[600],
          highlighted: globalColors.chromatic.sapphire[700],
          disabled: globalColors.chromatic.sapphire.a100,
          faded: globalColors.chromatic.sapphire.a100,
        },
        neutral: {
          default: globalColors.neutral.blueGrayLight[1200],
          highlighted: globalColors.neutral.blueGrayLight[1300],
          disabled: globalColors.neutral.blueGrayLight[300],
          faded: globalColors.neutral.blueGrayLight.a912,
        },
        gray: {
          default: globalColors.neutral.blueGrayLight[200],
          highlighted: globalColors.neutral.blueGrayLight[400],
          disabled: globalColors.neutral.blueGrayLight[200],
          faded: globalColors.neutral.blueGrayLight.a918,
        },
        primary: {
          default: globalColors.chromatic.azure[500],
          highlighted: globalColors.chromatic.azure[600],
          disabled: globalColors.chromatic.azure.a100,
          faded: globalColors.chromatic.azure.a100,
        },
        staticWhite: {
          default: globalColors.neutral.white[500],
          highlighted: globalColors.neutral.white[400],
          disabled: globalColors.neutral.white[100],
          faded: globalColors.neutral.white[50],
          fadedHighlighted: globalColors.neutral.white[100],
        },
        staticBlack: {
          default: globalColors.neutral.black[500],
          highlighted: globalColors.neutral.black[500],
          disabled: globalColors.neutral.black[100],
          faded: globalColors.neutral.black[100],
          fadedHighlighted: globalColors.neutral.black[50],
        },
      },
      text: {
        positive: {
          normal: globalColors.chromatic.emerald[700],
          subtle: globalColors.chromatic.emerald[600],
          muted: globalColors.chromatic.emerald[400],
          disabled: globalColors.chromatic.emerald.a200,
        },
        negative: {
          normal: globalColors.chromatic.crimson[600],
          subtle: globalColors.chromatic.crimson[500],
          muted: globalColors.chromatic.crimson[400],
          disabled: globalColors.chromatic.crimson.a200,
        },
        notice: {
          normal: globalColors.chromatic.cider[700],
          subtle: globalColors.chromatic.cider[600],
          muted: globalColors.chromatic.cider[400],
          disabled: globalColors.chromatic.cider.a200,
        },
        information: {
          normal: globalColors.chromatic.sapphire[700],
          subtle: globalColors.chromatic.sapphire[600],
          muted: globalColors.chromatic.sapphire[400],
          disabled: globalColors.chromatic.sapphire.a200,
        },
        neutral: {
          normal: globalColors.neutral.blueGrayLight[1300],
          subtle: globalColors.neutral.blueGrayLight[1100],
          muted: globalColors.neutral.blueGrayLight[700],
          disabled: globalColors.neutral.blueGrayLight.a932,
        },
        gray: {
          normal: globalColors.neutral.blueGrayLight[1300],
          subtle: globalColors.neutral.blueGrayLight[1100],
          muted: globalColors.neutral.blueGrayLight[700],
          disabled: globalColors.neutral.blueGrayLight.a932,
        },
        primary: {
          normal: globalColors.chromatic.azure[600],
          subtle: globalColors.chromatic.azure[500],
          muted: globalColors.chromatic.azure[400],
          disabled: globalColors.chromatic.azure.a200,
        },
        onPrimary: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[400],
          muted: globalColors.neutral.white[300],
          disabled: globalColors.neutral.white[100],
        },
        staticWhite: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[400],
          muted: globalColors.neutral.white[300],
          disabled: globalColors.neutral.white[100],
        },
        staticBlack: {
          normal: globalColors.neutral.black[500],
          subtle: globalColors.neutral.black[400],
          muted: globalColors.neutral.black[300],
          disabled: globalColors.neutral.black[100],
        },
      },
      icon: {
        positive: {
          normal: globalColors.chromatic.emerald[700],
          subtle: globalColors.chromatic.emerald[600],
          muted: globalColors.chromatic.emerald[400],
          disabled: globalColors.chromatic.emerald.a200,
        },
        negative: {
          normal: globalColors.chromatic.crimson[600],
          subtle: globalColors.chromatic.crimson[500],
          muted: globalColors.chromatic.crimson[400],
          disabled: globalColors.chromatic.crimson.a200,
        },
        notice: {
          normal: globalColors.chromatic.cider[700],
          subtle: globalColors.chromatic.cider[600],
          muted: globalColors.chromatic.cider[400],
          disabled: globalColors.chromatic.cider.a200,
        },
        information: {
          normal: globalColors.chromatic.sapphire[700],
          subtle: globalColors.chromatic.sapphire[600],
          muted: globalColors.chromatic.sapphire[400],
          disabled: globalColors.chromatic.sapphire.a200,
        },
        neutral: {
          normal: globalColors.neutral.blueGrayLight[1300],
          subtle: globalColors.neutral.blueGrayLight[1100],
          muted: globalColors.neutral.blueGrayLight[700],
          disabled: globalColors.neutral.blueGrayLight.a932,
        },
        gray: {
          normal: globalColors.neutral.blueGrayLight[1300],
          subtle: globalColors.neutral.blueGrayLight[1100],
          muted: globalColors.neutral.blueGrayLight[700],
          disabled: globalColors.neutral.blueGrayLight.a932,
        },
        primary: {
          normal: globalColors.chromatic.azure[600],
          subtle: globalColors.chromatic.azure[500],
          muted: globalColors.chromatic.azure[400],
          disabled: globalColors.chromatic.azure.a200,
        },
        onPrimary: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[400],
          muted: globalColors.neutral.white[300],
          disabled: globalColors.neutral.white[100],
        },
        staticWhite: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[400],
          muted: globalColors.neutral.white[300],
          disabled: globalColors.neutral.white[100],
        },
        staticBlack: {
          normal: globalColors.neutral.black[500],
          subtle: globalColors.neutral.black[400],
          muted: globalColors.neutral.black[300],
          disabled: globalColors.neutral.black[100],
        },
      },
    },
    overlay: {
      background: {
        moderate: globalColors.neutral.blueGrayLight.a918,
        subtle: globalColors.neutral.black[200],
      },
    },
    popup: {
      background: {
        // @deprecated
        subtle: globalColors.neutral.blueGrayLight[0],
        // @deprecated
        intense: globalColors.neutral.blueGrayLight[1000],
        gray: {
          subtle: globalColors.neutral.blueGrayLight[0],
          moderate: globalColors.neutral.blueGrayLight.a48,
          intense: globalColors.neutral.black[300],
        },
        positive: {
          moderate: globalColors.chromatic.emerald.a700,
        },
        negative: {
          moderate: globalColors.chromatic.crimson.a700,
        },
        notice: {
          moderate: globalColors.chromatic.cider.a700,
        },
        information: {
          moderate: globalColors.chromatic.sapphire.a700,
        },
        neutral: {
          moderate: globalColors.neutral.blueGrayLight.a1288,
        },
      },
      border: {
        // @deprecated
        subtle: globalColors.neutral.blueGrayLight.a100,
        // @deprecated
        intense: globalColors.neutral.blueGrayLight[900],
        gray: {
          subtle: globalColors.neutral.blueGrayLight.a909,
          moderate: globalColors.neutral.blueGrayLight[100],
          intense: globalColors.neutral.blueGrayLight[1000],
        },
        positive: {
          moderate: globalColors.chromatic.emerald[600],
        },
        negative: {
          moderate: globalColors.chromatic.crimson[600],
        },
        notice: {
          moderate: globalColors.chromatic.cider[600],
        },
        information: {
          moderate: globalColors.chromatic.sapphire[600],
        },
        neutral: {
          moderate: globalColors.neutral.blueGrayLight[1100],
        },
      },
    },
    data: {
      background: {
        categorical: {
          blue: {
            faint: globalColors.chromatic.azure[100],
            subtle: globalColors.chromatic.azure[200],
            moderate: globalColors.chromatic.azure[300],
            intense: globalColors.chromatic.azure[400],
            strong: globalColors.chromatic.azure[500],
          },
          green: {
            faint: globalColors.chromatic.emerald[100],
            subtle: globalColors.chromatic.emerald[200],
            moderate: globalColors.chromatic.emerald[300],
            intense: globalColors.chromatic.emerald[400],
            strong: globalColors.chromatic.emerald[500],
          },
          red: {
            faint: globalColors.chromatic.crimson[100],
            subtle: globalColors.chromatic.crimson[200],
            moderate: globalColors.chromatic.crimson[300],
            intense: globalColors.chromatic.crimson[400],
            strong: globalColors.chromatic.crimson[500],
          },
          orange: {
            faint: globalColors.chromatic.cider[100],
            subtle: globalColors.chromatic.cider[200],
            moderate: globalColors.chromatic.cider[300],
            intense: globalColors.chromatic.cider[400],
            strong: globalColors.chromatic.cider[500],
          },
          skyBlue: {
            faint: globalColors.chromatic.sapphire[100],
            subtle: globalColors.chromatic.sapphire[200],
            moderate: globalColors.chromatic.sapphire[300],
            intense: globalColors.chromatic.sapphire[400],
            strong: globalColors.chromatic.sapphire[500],
          },
          purple: {
            faint: globalColors.chromatic.orchid[100],
            subtle: globalColors.chromatic.orchid[200],
            moderate: globalColors.chromatic.orchid[300],
            intense: globalColors.chromatic.orchid[400],
            strong: globalColors.chromatic.orchid[500],
          },
          pink: {
            faint: globalColors.chromatic.magenta[100],
            subtle: globalColors.chromatic.magenta[200],
            moderate: globalColors.chromatic.magenta[300],
            intense: globalColors.chromatic.magenta[400],
            strong: globalColors.chromatic.magenta[500],
          },
          gold: {
            faint: globalColors.chromatic.topaz[50],
            subtle: globalColors.chromatic.topaz[100],
            moderate: globalColors.chromatic.topaz[200],
            intense: globalColors.chromatic.topaz[300],
            strong: globalColors.chromatic.topaz[400],
          },
          gray: {
            faint: globalColors.neutral.blueGrayLight[0],
            subtle: globalColors.neutral.blueGrayLight[50],
            moderate: globalColors.neutral.blueGrayLight[100],
            intense: globalColors.neutral.blueGrayLight[400],
            strong: globalColors.neutral.blueGrayLight[700],
          },
        },
        sequential: {
          blue: {
            50: globalColors.chromatic.azure[50],
            100: globalColors.chromatic.azure[100],
            200: globalColors.chromatic.azure[200],
            300: globalColors.chromatic.azure[300],
            400: globalColors.chromatic.azure[400],
            500: globalColors.chromatic.azure[500],
            600: globalColors.chromatic.azure[600],
            700: globalColors.chromatic.azure[700],
            800: globalColors.chromatic.azure[800],
            900: globalColors.chromatic.azure[900],
            1000: globalColors.chromatic.azure[1000],
          },
          green: {
            50: globalColors.chromatic.emerald[50],
            100: globalColors.chromatic.emerald[100],
            200: globalColors.chromatic.emerald[200],
            300: globalColors.chromatic.emerald[300],
            400: globalColors.chromatic.emerald[400],
            500: globalColors.chromatic.emerald[500],
            600: globalColors.chromatic.emerald[600],
            700: globalColors.chromatic.emerald[700],
            800: globalColors.chromatic.emerald[800],
            900: globalColors.chromatic.emerald[900],
            1000: globalColors.chromatic.emerald[1000],
          },
          red: {
            50: globalColors.chromatic.crimson[50],
            100: globalColors.chromatic.crimson[100],
            200: globalColors.chromatic.crimson[200],
            300: globalColors.chromatic.crimson[300],
            400: globalColors.chromatic.crimson[400],
            500: globalColors.chromatic.crimson[500],
            600: globalColors.chromatic.crimson[600],
            700: globalColors.chromatic.crimson[700],
            800: globalColors.chromatic.crimson[800],
            900: globalColors.chromatic.crimson[900],
            1000: globalColors.chromatic.crimson[1000],
          },
          orange: {
            50: globalColors.chromatic.cider[50],
            100: globalColors.chromatic.cider[100],
            200: globalColors.chromatic.cider[200],
            300: globalColors.chromatic.cider[300],
            400: globalColors.chromatic.cider[400],
            500: globalColors.chromatic.cider[500],
            600: globalColors.chromatic.cider[600],
            700: globalColors.chromatic.cider[700],
            800: globalColors.chromatic.cider[800],
            900: globalColors.chromatic.cider[900],
            1000: globalColors.chromatic.cider[1000],
          },
          skyBlue: {
            50: globalColors.chromatic.sapphire[50],
            100: globalColors.chromatic.sapphire[100],
            200: globalColors.chromatic.sapphire[200],
            300: globalColors.chromatic.sapphire[300],
            400: globalColors.chromatic.sapphire[400],
            500: globalColors.chromatic.sapphire[500],
            600: globalColors.chromatic.sapphire[600],
            700: globalColors.chromatic.sapphire[700],
            800: globalColors.chromatic.sapphire[800],
            900: globalColors.chromatic.sapphire[900],
            1000: globalColors.chromatic.sapphire[1000],
          },
          purple: {
            50: globalColors.chromatic.orchid[50],
            100: globalColors.chromatic.orchid[100],
            200: globalColors.chromatic.orchid[200],
            300: globalColors.chromatic.orchid[300],
            400: globalColors.chromatic.orchid[400],
            500: globalColors.chromatic.orchid[500],
            600: globalColors.chromatic.orchid[600],
            700: globalColors.chromatic.orchid[700],
            800: globalColors.chromatic.orchid[800],
            900: globalColors.chromatic.orchid[900],
            1000: globalColors.chromatic.orchid[1000],
          },
          pink: {
            50: globalColors.chromatic.magenta[50],
            100: globalColors.chromatic.magenta[100],
            200: globalColors.chromatic.magenta[200],
            300: globalColors.chromatic.magenta[300],
            400: globalColors.chromatic.magenta[400],
            500: globalColors.chromatic.magenta[500],
            600: globalColors.chromatic.magenta[600],
            700: globalColors.chromatic.magenta[700],
            800: globalColors.chromatic.magenta[800],
            900: globalColors.chromatic.magenta[900],
            1000: globalColors.chromatic.magenta[1000],
          },
          gold: {
            50: globalColors.chromatic.topaz[50],
            100: globalColors.chromatic.topaz[100],
            200: globalColors.chromatic.topaz[200],
            300: globalColors.chromatic.topaz[300],
            400: globalColors.chromatic.topaz[400],
            500: globalColors.chromatic.topaz[500],
            600: globalColors.chromatic.topaz[600],
            700: globalColors.chromatic.topaz[700],
            800: globalColors.chromatic.topaz[800],
            900: globalColors.chromatic.topaz[900],
            1000: globalColors.chromatic.topaz[1000],
          },
          gray: {
            0: globalColors.neutral.blueGrayLight[0],
            50: globalColors.neutral.blueGrayLight[50],
            100: globalColors.neutral.blueGrayLight[100],
            200: globalColors.neutral.blueGrayLight[200],
            300: globalColors.neutral.blueGrayLight[300],
            400: globalColors.neutral.blueGrayLight[400],
            500: globalColors.neutral.blueGrayLight[500],
            600: globalColors.neutral.blueGrayLight[600],
            700: globalColors.neutral.blueGrayLight[700],
            800: globalColors.neutral.blueGrayLight[800],
            900: globalColors.neutral.blueGrayLight[900],
            1000: globalColors.neutral.blueGrayLight[1000],
          },
        },
      },
    },
    transparent: `hsla(0, 0%, 100%, ${opacity[0]})`,
  },
  onDark: {
    surface: {
      background: {
        gray: {
          subtle: globalColors.neutral.blueGrayDark[1300],
          moderate: globalColors.neutral.blueGrayDark[1200],
          intense: globalColors.neutral.blueGrayDark[1100],
        },
        primary: {
          subtle: globalColors.chromatic.azure.a200,
          intense: globalColors.chromatic.azure[500],
        },
        sea: {
          subtle: globalColors.chromatic.sea[900],
          intense: globalColors.chromatic.sea[100],
        },
        cloud: {
          subtle: globalColors.chromatic.cloud[900],
          intense: globalColors.chromatic.cloud[100],
        },
      },
      border: {
        gray: {
          normal: globalColors.neutral.blueGrayDark[600],
          subtle: globalColors.neutral.blueGrayDark[800],
          muted: globalColors.neutral.blueGrayDark.a518,
        },
        primary: {
          normal: globalColors.chromatic.azure[500],
          muted: globalColors.chromatic.azure.a200,
        },
      },
      text: {
        gray: {
          normal: globalColors.neutral.blueGrayDark[0],
          subtle: globalColors.neutral.blueGrayDark[300],
          muted: globalColors.neutral.blueGrayDark[500],
          disabled: globalColors.neutral.blueGrayDark.a564,
        },
        primary: {
          normal: globalColors.chromatic.azure[300],
        },
        onSea: {
          onSubtle: globalColors.chromatic.forest[200],
          onIntense: globalColors.chromatic.forest[800],
        },
        onCloud: {
          onSubtle: globalColors.chromatic.azure[200],
          onIntense: globalColors.chromatic.azure[600],
        },
        staticWhite: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[450],
          muted: globalColors.neutral.white[200],
          disabled: globalColors.neutral.white[100],
        },
        staticBlack: {
          normal: globalColors.neutral.black[500],
          subtle: globalColors.neutral.black[300],
          muted: globalColors.neutral.black[200],
          disabled: globalColors.neutral.black[100],
        },
      },
      icon: {
        gray: {
          normal: globalColors.neutral.blueGrayDark[0],
          subtle: globalColors.neutral.blueGrayDark[300],
          muted: globalColors.neutral.blueGrayDark[500],
          disabled: globalColors.neutral.blueGrayDark.a532,
        },
        primary: {
          normal: globalColors.chromatic.azure[300],
        },
        onSea: {
          onSubtle: globalColors.chromatic.forest[400],
          onIntense: globalColors.chromatic.forest[600],
        },
        onCloud: {
          onSubtle: globalColors.chromatic.azure[300],
          onIntense: globalColors.chromatic.azure[400],
        },
        staticWhite: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[450],
          muted: globalColors.neutral.white[200],
          disabled: globalColors.neutral.white[100],
        },
        staticBlack: {
          normal: globalColors.neutral.black[500],
          subtle: globalColors.neutral.black[300],
          muted: globalColors.neutral.black[200],
          disabled: globalColors.neutral.black[100],
        },
      },
    },
    feedback: {
      background: {
        positive: {
          subtle: globalColors.chromatic.emerald.a100,
          intense: globalColors.chromatic.emerald[700],
        },
        negative: {
          subtle: globalColors.chromatic.crimson.a100,
          intense: globalColors.chromatic.crimson[700],
        },
        notice: {
          subtle: globalColors.chromatic.cider.a100,
          intense: globalColors.chromatic.cider[700],
        },
        information: {
          subtle: globalColors.chromatic.sapphire.a100,
          intense: globalColors.chromatic.sapphire[700],
        },
        neutral: {
          subtle: globalColors.neutral.blueGrayDark.a509,
          intense: globalColors.neutral.blueGrayDark[800],
        },
      },
      border: {
        positive: {
          subtle: globalColors.chromatic.emerald.a200,
          intense: globalColors.chromatic.emerald[800],
        },
        negative: {
          subtle: globalColors.chromatic.crimson.a200,
          intense: globalColors.chromatic.crimson[800],
        },
        notice: {
          subtle: globalColors.chromatic.cider.a200,
          intense: globalColors.chromatic.cider[800],
        },
        information: {
          subtle: globalColors.chromatic.sapphire.a200,
          intense: globalColors.chromatic.sapphire[800],
        },
        neutral: {
          subtle: globalColors.neutral.blueGrayDark.a518,
          intense: globalColors.neutral.blueGrayDark[800],
        },
      },
      text: {
        positive: {
          subtle: globalColors.chromatic.emerald[50],
          intense: globalColors.chromatic.emerald[400],
        },
        negative: {
          subtle: globalColors.chromatic.crimson[50],
          intense: globalColors.chromatic.crimson[400],
        },
        notice: {
          subtle: globalColors.chromatic.cider[50],
          intense: globalColors.chromatic.cider[400],
        },
        information: {
          subtle: globalColors.chromatic.sapphire[50],
          intense: globalColors.chromatic.sapphire[400],
        },
        neutral: {
          subtle: globalColors.neutral.blueGrayDark[500],
          intense: globalColors.neutral.blueGrayDark[300],
        },
      },
      icon: {
        positive: {
          subtle: globalColors.chromatic.emerald[50],
          intense: globalColors.chromatic.emerald[400],
        },
        negative: {
          subtle: globalColors.chromatic.crimson[50],
          intense: globalColors.chromatic.crimson[400],
        },
        notice: {
          subtle: globalColors.chromatic.cider[50],
          intense: globalColors.chromatic.cider[400],
        },
        information: {
          subtle: globalColors.chromatic.sapphire[50],
          intense: globalColors.chromatic.sapphire[400],
        },
        neutral: {
          subtle: globalColors.neutral.blueGrayDark[500],
          intense: globalColors.neutral.blueGrayDark[300],
        },
      },
    },
    interactive: {
      background: {
        positive: {
          default: globalColors.chromatic.emerald[600],
          highlighted: globalColors.chromatic.emerald[700],
          disabled: globalColors.chromatic.emerald.a100,
          faded: globalColors.chromatic.emerald.a150,
          fadedHighlighted: globalColors.chromatic.emerald.a200,
        },
        negative: {
          default: globalColors.chromatic.crimson[600],
          highlighted: globalColors.chromatic.crimson[700],
          disabled: globalColors.chromatic.crimson.a100,
          faded: globalColors.chromatic.crimson.a150,
          fadedHighlighted: globalColors.chromatic.crimson.a200,
        },
        notice: {
          default: globalColors.chromatic.cider[600],
          highlighted: globalColors.chromatic.cider[700],
          disabled: globalColors.chromatic.cider.a100,
          faded: globalColors.chromatic.cider.a150,
          fadedHighlighted: globalColors.chromatic.cider.a200,
        },
        information: {
          default: globalColors.chromatic.sapphire[600],
          highlighted: globalColors.chromatic.sapphire[700],
          disabled: globalColors.chromatic.sapphire.a100,
          faded: globalColors.chromatic.sapphire.a150,
          fadedHighlighted: globalColors.chromatic.sapphire.a200,
        },
        neutral: {
          default: globalColors.neutral.blueGrayDark[50],
          highlighted: globalColors.neutral.blueGrayDark[200],
          disabled: globalColors.neutral.blueGrayDark.a518,
          faded: globalColors.neutral.blueGrayDark.a512,
          fadedHighlighted: globalColors.neutral.blueGrayDark.a518,
        },
        gray: {
          default: globalColors.neutral.blueGrayDark.a512,
          highlighted: globalColors.neutral.blueGrayDark.a532,
          disabled: globalColors.neutral.blueGrayDark.a509,
          faded: globalColors.neutral.blueGrayDark.a509,
          fadedHighlighted: globalColors.neutral.blueGrayDark.a518,
          ghost: globalColors.neutral.blueGrayDark.a1388,
        },
        primary: {
          default: globalColors.chromatic.azure[400],
          highlighted: globalColors.chromatic.azure[500],
          disabled: globalColors.chromatic.azure.a150,
          faded: globalColors.chromatic.azure.a150,
          fadedHighlighted: globalColors.chromatic.azure.a200,
        },
        staticBlack: {
          default: globalColors.neutral.black[500],
          highlighted: globalColors.neutral.black[500],
          disabled: globalColors.neutral.black[200],
          faded: globalColors.neutral.black[50],
          fadedHighlighted: globalColors.neutral.black[100],
          ghost: globalColors.neutral.black[1],
        },
        staticWhite: {
          default: globalColors.neutral.white[500],
          highlighted: globalColors.neutral.white[400],
          disabled: globalColors.neutral.white[50],
          faded: globalColors.neutral.white[50],
          fadedHighlighted: globalColors.neutral.white[100],
          ghost: globalColors.neutral.white[1],
        },
      },
      border: {
        positive: {
          default: globalColors.chromatic.emerald[600],
          highlighted: globalColors.chromatic.emerald[700],
          disabled: globalColors.chromatic.emerald.a100,
          faded: globalColors.chromatic.emerald.a100,
        },
        negative: {
          default: globalColors.chromatic.crimson[600],
          highlighted: globalColors.chromatic.crimson[700],
          disabled: globalColors.chromatic.crimson.a100,
          faded: globalColors.chromatic.crimson.a100,
        },
        notice: {
          default: globalColors.chromatic.cider[600],
          highlighted: globalColors.chromatic.cider[700],
          disabled: globalColors.chromatic.cider.a100,
          faded: globalColors.chromatic.cider.a100,
        },
        information: {
          default: globalColors.chromatic.sapphire[600],
          highlighted: globalColors.chromatic.sapphire[700],
          disabled: globalColors.chromatic.sapphire.a100,
          faded: globalColors.chromatic.sapphire.a100,
        },
        neutral: {
          default: globalColors.neutral.blueGrayDark[100],
          highlighted: globalColors.neutral.blueGrayDark[0],
          disabled: globalColors.neutral.blueGrayDark[800],
          faded: globalColors.neutral.blueGrayDark.a518,
        },
        gray: {
          default: globalColors.neutral.blueGrayDark[800],
          highlighted: globalColors.neutral.blueGrayDark[700],
          disabled: globalColors.neutral.blueGrayDark[1000],
          faded: globalColors.neutral.blueGrayDark.a518,
        },
        primary: {
          default: globalColors.chromatic.azure[400],
          highlighted: globalColors.chromatic.azure[600],
          disabled: globalColors.chromatic.azure.a200,
          faded: globalColors.chromatic.azure.a150,
        },
        staticWhite: {
          default: globalColors.neutral.white[500],
          highlighted: globalColors.neutral.white[400],
          disabled: globalColors.neutral.white[100],
          faded: globalColors.neutral.white[50],
          fadedHighlighted: globalColors.neutral.white[100],
        },
        staticBlack: {
          default: globalColors.neutral.black[500],
          highlighted: globalColors.neutral.black[500],
          disabled: globalColors.neutral.black[100],
          faded: globalColors.neutral.black[100],
          fadedHighlighted: globalColors.neutral.black[50],
        },
      },
      text: {
        positive: {
          normal: globalColors.chromatic.emerald[400],
          subtle: globalColors.chromatic.emerald[500],
          muted: globalColors.chromatic.emerald[700],
          disabled: globalColors.chromatic.emerald.a200,
        },
        negative: {
          normal: globalColors.chromatic.crimson[400],
          subtle: globalColors.chromatic.crimson[500],
          muted: globalColors.chromatic.crimson[700],
          disabled: globalColors.chromatic.crimson.a200,
        },
        notice: {
          normal: globalColors.chromatic.cider[400],
          subtle: globalColors.chromatic.cider[500],
          muted: globalColors.chromatic.cider[700],
          disabled: globalColors.chromatic.cider.a200,
        },
        information: {
          normal: globalColors.chromatic.sapphire[400],
          subtle: globalColors.chromatic.sapphire[500],
          muted: globalColors.chromatic.sapphire[700],
          disabled: globalColors.chromatic.sapphire.a200,
        },
        neutral: {
          normal: globalColors.neutral.blueGrayDark[0],
          subtle: globalColors.neutral.blueGrayDark[300],
          muted: globalColors.neutral.blueGrayDark[500],
          disabled: globalColors.neutral.blueGrayDark.a532,
        },
        gray: {
          normal: globalColors.neutral.blueGrayDark[0],
          subtle: globalColors.neutral.blueGrayDark[300],
          muted: globalColors.neutral.blueGrayDark[500],
          disabled: globalColors.neutral.blueGrayDark.a532,
        },
        primary: {
          normal: globalColors.chromatic.azure[300],
          subtle: globalColors.chromatic.azure[400],
          muted: globalColors.chromatic.azure[600],
          disabled: globalColors.chromatic.azure.a400,
        },
        onPrimary: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[400],
          muted: globalColors.neutral.white[300],
          disabled: globalColors.neutral.white[100],
        },
        staticWhite: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[400],
          muted: globalColors.neutral.white[300],
          disabled: globalColors.neutral.white[100],
        },
        staticBlack: {
          normal: globalColors.neutral.black[500],
          subtle: globalColors.neutral.black[400],
          muted: globalColors.neutral.black[300],
          disabled: globalColors.neutral.black[100],
        },
      },
      icon: {
        positive: {
          normal: globalColors.chromatic.emerald[400],
          subtle: globalColors.chromatic.emerald[500],
          muted: globalColors.chromatic.emerald[700],
          disabled: globalColors.chromatic.emerald.a200,
        },
        negative: {
          normal: globalColors.chromatic.crimson[400],
          subtle: globalColors.chromatic.crimson[500],
          muted: globalColors.chromatic.crimson[700],
          disabled: globalColors.chromatic.crimson.a200,
        },
        notice: {
          normal: globalColors.chromatic.cider[400],
          subtle: globalColors.chromatic.cider[500],
          muted: globalColors.chromatic.cider[700],
          disabled: globalColors.chromatic.cider.a200,
        },
        information: {
          normal: globalColors.chromatic.sapphire[400],
          subtle: globalColors.chromatic.sapphire[500],
          muted: globalColors.chromatic.sapphire[700],
          disabled: globalColors.chromatic.sapphire.a200,
        },
        neutral: {
          normal: globalColors.neutral.blueGrayDark[0],
          subtle: globalColors.neutral.blueGrayDark[300],
          muted: globalColors.neutral.blueGrayDark[500],
          disabled: globalColors.neutral.blueGrayDark.a532,
        },
        gray: {
          normal: globalColors.neutral.blueGrayDark[0],
          subtle: globalColors.neutral.blueGrayDark[300],
          muted: globalColors.neutral.blueGrayDark[500],
          disabled: globalColors.neutral.blueGrayDark.a532,
        },
        primary: {
          normal: globalColors.chromatic.azure[300],
          subtle: globalColors.chromatic.azure[400],
          muted: globalColors.chromatic.azure[600],
          disabled: globalColors.chromatic.azure.a400,
        },
        onPrimary: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[400],
          muted: globalColors.neutral.white[300],
          disabled: globalColors.neutral.white[100],
        },
        staticWhite: {
          normal: globalColors.neutral.white[500],
          subtle: globalColors.neutral.white[400],
          muted: globalColors.neutral.white[300],
          disabled: globalColors.neutral.white[100],
        },
        staticBlack: {
          normal: globalColors.neutral.black[500],
          subtle: globalColors.neutral.black[400],
          muted: globalColors.neutral.black[300],
          disabled: globalColors.neutral.black[100],
        },
      },
    },
    overlay: {
      background: {
        moderate: globalColors.neutral.blueGrayDark.a518,
        subtle: globalColors.neutral.black[400],
      },
    },
    popup: {
      background: {
        // @deprecated
        subtle: globalColors.neutral.blueGrayDark[1000],
        // @deprecated
        intense: globalColors.neutral.blueGrayDark[700],
        gray: {
          subtle: globalColors.neutral.blueGrayDark[1300],
          moderate: globalColors.neutral.blueGrayDark.a1188,
          intense: globalColors.neutral.black[300],
        },
        positive: {
          moderate: globalColors.chromatic.emerald.a700,
        },
        negative: {
          moderate: globalColors.chromatic.crimson.a700,
        },
        notice: {
          moderate: globalColors.chromatic.cider.a700,
        },
        information: {
          moderate: globalColors.chromatic.sapphire.a700,
        },
        neutral: {
          moderate: globalColors.neutral.blueGrayDark.a888,
        },
      },
      border: {
        // @deprecated
        subtle: globalColors.neutral.blueGrayDark.a100,
        // @deprecated
        intense: globalColors.neutral.blueGrayDark.a100,
        gray: {
          subtle: globalColors.neutral.blueGrayDark.a512,
          moderate: globalColors.neutral.blueGrayDark[800],
          intense: globalColors.neutral.blueGrayDark[1000],
        },
        positive: {
          moderate: globalColors.chromatic.emerald[600],
        },
        negative: {
          moderate: globalColors.chromatic.crimson[600],
        },
        notice: {
          moderate: globalColors.chromatic.cider[600],
        },
        information: {
          moderate: globalColors.chromatic.sapphire[600],
        },
        neutral: {
          moderate: globalColors.neutral.blueGrayDark[800],
        },
      },
    },
    data: {
      background: {
        categorical: {
          blue: {
            faint: globalColors.chromatic.azure[800],
            subtle: globalColors.chromatic.azure[700],
            moderate: globalColors.chromatic.azure[600],
            intense: globalColors.chromatic.azure[500],
            strong: globalColors.chromatic.azure[400],
          },
          green: {
            faint: globalColors.chromatic.emerald[800],
            subtle: globalColors.chromatic.emerald[700],
            moderate: globalColors.chromatic.emerald[600],
            intense: globalColors.chromatic.emerald[500],
            strong: globalColors.chromatic.emerald[400],
          },
          red: {
            faint: globalColors.chromatic.crimson[800],
            subtle: globalColors.chromatic.crimson[700],
            moderate: globalColors.chromatic.crimson[600],
            intense: globalColors.chromatic.crimson[500],
            strong: globalColors.chromatic.crimson[400],
          },
          orange: {
            faint: globalColors.chromatic.cider[800],
            subtle: globalColors.chromatic.cider[700],
            moderate: globalColors.chromatic.cider[600],
            intense: globalColors.chromatic.cider[500],
            strong: globalColors.chromatic.cider[400],
          },
          skyBlue: {
            faint: globalColors.chromatic.sapphire[800],
            subtle: globalColors.chromatic.sapphire[700],
            moderate: globalColors.chromatic.sapphire[600],
            intense: globalColors.chromatic.sapphire[500],
            strong: globalColors.chromatic.sapphire[400],
          },
          purple: {
            faint: globalColors.chromatic.orchid[800],
            subtle: globalColors.chromatic.orchid[700],
            moderate: globalColors.chromatic.orchid[600],
            intense: globalColors.chromatic.orchid[500],
            strong: globalColors.chromatic.orchid[400],
          },
          pink: {
            faint: globalColors.chromatic.magenta[800],
            subtle: globalColors.chromatic.magenta[700],
            moderate: globalColors.chromatic.magenta[600],
            intense: globalColors.chromatic.magenta[500],
            strong: globalColors.chromatic.magenta[400],
          },
          gold: {
            faint: globalColors.chromatic.topaz[700],
            subtle: globalColors.chromatic.topaz[600],
            moderate: globalColors.chromatic.topaz[500],
            intense: globalColors.chromatic.topaz[400],
            strong: globalColors.chromatic.topaz[300],
          },
          gray: {
            faint: globalColors.neutral.blueGrayDark[1200],
            subtle: globalColors.neutral.blueGrayDark[1000],
            moderate: globalColors.neutral.blueGrayDark[900],
            intense: globalColors.neutral.blueGrayDark[300],
            strong: globalColors.neutral.blueGrayDark[600],
          },
        },
        sequential: {
          blue: {
            50: globalColors.chromatic.azure[1000],
            100: globalColors.chromatic.azure[900],
            200: globalColors.chromatic.azure[800],
            300: globalColors.chromatic.azure[700],
            400: globalColors.chromatic.azure[600],
            500: globalColors.chromatic.azure[500],
            600: globalColors.chromatic.azure[400],
            700: globalColors.chromatic.azure[300],
            800: globalColors.chromatic.azure[200],
            900: globalColors.chromatic.azure[100],
            1000: globalColors.chromatic.azure[50],
          },
          green: {
            50: globalColors.chromatic.emerald[1000],
            100: globalColors.chromatic.emerald[900],
            200: globalColors.chromatic.emerald[800],
            300: globalColors.chromatic.emerald[700],
            400: globalColors.chromatic.emerald[600],
            500: globalColors.chromatic.emerald[500],
            600: globalColors.chromatic.emerald[400],
            700: globalColors.chromatic.emerald[300],
            800: globalColors.chromatic.emerald[200],
            900: globalColors.chromatic.emerald[100],
            1000: globalColors.chromatic.emerald[50],
          },
          red: {
            50: globalColors.chromatic.crimson[1000],
            100: globalColors.chromatic.crimson[900],
            200: globalColors.chromatic.crimson[800],
            300: globalColors.chromatic.crimson[700],
            400: globalColors.chromatic.crimson[600],
            500: globalColors.chromatic.crimson[500],
            600: globalColors.chromatic.crimson[400],
            700: globalColors.chromatic.crimson[300],
            800: globalColors.chromatic.crimson[200],
            900: globalColors.chromatic.crimson[100],
            1000: globalColors.chromatic.crimson[50],
          },
          orange: {
            50: globalColors.chromatic.cider[1000],
            100: globalColors.chromatic.cider[900],
            200: globalColors.chromatic.cider[800],
            300: globalColors.chromatic.cider[700],
            400: globalColors.chromatic.cider[600],
            500: globalColors.chromatic.cider[500],
            600: globalColors.chromatic.cider[400],
            700: globalColors.chromatic.cider[300],
            800: globalColors.chromatic.cider[200],
            900: globalColors.chromatic.cider[100],
            1000: globalColors.chromatic.cider[50],
          },
          skyBlue: {
            50: globalColors.chromatic.sapphire[1000],
            100: globalColors.chromatic.sapphire[900],
            200: globalColors.chromatic.sapphire[800],
            300: globalColors.chromatic.sapphire[700],
            400: globalColors.chromatic.sapphire[600],
            500: globalColors.chromatic.sapphire[500],
            600: globalColors.chromatic.sapphire[400],
            700: globalColors.chromatic.sapphire[300],
            800: globalColors.chromatic.sapphire[200],
            900: globalColors.chromatic.sapphire[100],
            1000: globalColors.chromatic.sapphire[50],
          },
          purple: {
            50: globalColors.chromatic.orchid[1000],
            100: globalColors.chromatic.orchid[900],
            200: globalColors.chromatic.orchid[800],
            300: globalColors.chromatic.orchid[700],
            400: globalColors.chromatic.orchid[600],
            500: globalColors.chromatic.orchid[500],
            600: globalColors.chromatic.orchid[400],
            700: globalColors.chromatic.orchid[300],
            800: globalColors.chromatic.orchid[200],
            900: globalColors.chromatic.orchid[100],
            1000: globalColors.chromatic.orchid[50],
          },
          pink: {
            50: globalColors.chromatic.magenta[1000],
            100: globalColors.chromatic.magenta[900],
            200: globalColors.chromatic.magenta[800],
            300: globalColors.chromatic.magenta[700],
            400: globalColors.chromatic.magenta[600],
            500: globalColors.chromatic.magenta[500],
            600: globalColors.chromatic.magenta[400],
            700: globalColors.chromatic.magenta[300],
            800: globalColors.chromatic.magenta[200],
            900: globalColors.chromatic.magenta[100],
            1000: globalColors.chromatic.magenta[50],
          },
          gold: {
            50: globalColors.chromatic.topaz[1000],
            100: globalColors.chromatic.topaz[900],
            200: globalColors.chromatic.topaz[800],
            300: globalColors.chromatic.topaz[700],
            400: globalColors.chromatic.topaz[600],
            500: globalColors.chromatic.topaz[500],
            600: globalColors.chromatic.topaz[400],
            700: globalColors.chromatic.topaz[300],
            800: globalColors.chromatic.topaz[200],
            900: globalColors.chromatic.topaz[100],
            1000: globalColors.chromatic.topaz[50],
          },
          gray: {
            0: globalColors.neutral.blueGrayDark[1300],
            50: globalColors.neutral.blueGrayDark[1200],
            100: globalColors.neutral.blueGrayDark[1100],
            200: globalColors.neutral.blueGrayDark[1000],
            300: globalColors.neutral.blueGrayDark[900],
            400: globalColors.neutral.blueGrayDark[800],
            500: globalColors.neutral.blueGrayDark[800],
            600: globalColors.neutral.blueGrayDark[700],
            700: globalColors.neutral.blueGrayDark[600],
            800: globalColors.neutral.blueGrayDark[500],
            900: globalColors.neutral.blueGrayDark[400],
            1000: globalColors.neutral.blueGrayDark[300],
          },
        },
      },
    },
    transparent: `hsla(210, 6%, 13%, ${opacity[0]})`,
  },
};

const bladeTheme: ThemeTokens = {
  name: 'bladeTheme',
  border,
  backdropBlur,
  breakpoints,
  colors,
  motion,
  spacing,
  elevation,
  typography,
};

export default bladeTheme;
